import AddIcon from '@mui/icons-material/Add';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { memo, ReactElement } from 'react';

import { linkedDocumentationAddTileButtonSx } from 'components/common/graphicUploader/styles';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { Color } from 'shared/utils/colors';

/** Same size as the attachments `addTileCompactIconSize`. */
const ADD_ICON_SIZE_PX = 32;

const tileCellSx = {
  flexShrink: 0,
  alignSelf: 'start',
  display: 'flex',
} satisfies SxProps<Theme>;

const buttonSx = {
  ...linkedDocumentationAddTileButtonSx,
  '&.Mui-disabled': {
    color: 'text.disabled',
  },
} satisfies SxProps<Theme>;

// Brand-yellow plus, like the attachments upload tile; a disabled tile keeps
// the neutral disabled color from `buttonSx`.
const iconSx = {
  fontSize: ADD_ICON_SIZE_PX,
  color: Color.primaryOrange,
  '.Mui-disabled &': { color: 'inherit' },
} satisfies SxProps<Theme>;

// Fills the tile so the "forbidden" hover area is the whole tile.
const forbiddenWrapperStyle = { display: 'flex', width: '100%', height: '100%' };

interface IssueAddTileProps {
  label: string;
  onClick: () => void;
  disabled: boolean;
  /** Shown on hover when creating is not allowed (e.g. finished inspection). */
  forbiddenReason?: string;
  /** Side of the square, in px. */
  size: number;
  dataQa: string;
}

function IssueAddTile({
  label,
  onClick,
  disabled,
  forbiddenReason,
  size,
  dataQa,
}: IssueAddTileProps): ReactElement {
  return (
    <Box sx={tileCellSx} style={{ width: size, height: size }}>
      <MemoForbiddenTooltip
        visibleWhen={Boolean(forbiddenReason)}
        reason={forbiddenReason || ''}
        style={forbiddenWrapperStyle}
      >
        <Tooltip title={disabled ? '' : label}>
          <ButtonBase
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            data-qa={dataQa}
            sx={buttonSx}
          >
            <AddIcon sx={iconSx} />
          </ButtonBase>
        </Tooltip>
      </MemoForbiddenTooltip>
    </Box>
  );
}

export const MemoIssueAddTile = memo(IssueAddTile);
